import { useEffect, useMemo, useState } from "react";
import { getOvhList, importOvh } from "../../../../api/ovh";
import {
  Accordion,
  Button,
  Checkbox,
  Group,
  Loader,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconDatabaseImport,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import BackButton from "../../../../components/View/BackButton";
import { searchNormalize } from "../../../../functions/utilFunctions";
import { useLanguage } from "../../../../stores/LanguageStore";

const OvhDomainList = ({ apiKey, domain, setDomain }) => {
  const lang = useLanguage((s) => s.language);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchText, setSearchText] = useState("");

  const handleSync = () => {
    setSyncing(true);
    importOvh({ external_api_key_id: parseInt(apiKey) })
      .then(() => {
        modals.open({
          modalId: "sync-success",
          title: lang.ovh.step2.sync_title,
          centered: true,
          children: (
            <Stack>
              <Text>{lang.ovh.step2.sync_text}</Text>

              <Group justify="end">
                <BackButton
                  size="md"
                  onClick={() => modals.close("sync-success")}
                />
              </Group>
            </Stack>
          ),
        });
        setLoading(true);
      })
      .finally(() => setSyncing(false));
  };

  const searchResult = useMemo(() => {
    if (searchText) {
      return list.filter((item) => {
        return searchNormalize(item.domain).includes(
          searchNormalize(searchText)
        );
      });
    }
    return list;
  }, [searchText, list]);

  useEffect(() => {
    if (loading) {
      getOvhList()
        .then((response) => {
          setList(response.data.list);
        })
        .finally(() => setLoading(false));
    }
  }, [loading]);

  return (
    <Stack pos={"relative"} mih={100}>
      <LoadingOverlay visible={loading} />
      <Group justify="end">
        <TextInput
          rightSection={<IconSearch size={18} />}
          mr={"auto"}
          size="md"
          placeholder={lang.ovh.step2.search_domain}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Button
          leftSection={<IconDatabaseImport size={18} />}
          onClick={handleSync}
          loading={syncing}
          variant="light"
        >
          {lang.ovh.step2.import_ovh}
        </Button>
        <Button
          variant="light"
          leftSection={<IconRefresh size={18} />}
          onClick={() => setLoading(true)}
        >
          {lang.ovh.step2.refresh}
        </Button>
      </Group>
      {searchResult.length > 0 ? (
        <Stack style={{ overflow: "auto" }} mah={"30vh"}>
          <Accordion variant="contained">
            {searchResult.map((item) => (
              <DomainItem
                key={item.domain}
                item={item}
                setLoading={setLoading}
                domain={domain}
                setDomain={setDomain}
              />
            ))}
          </Accordion>
        </Stack>
      ) : (
        <Text size="lg" ta={"center"} c={"dimmed"}>
          {lang.ovh.step2.no_domain}
        </Text>
      )}
    </Stack>
  );
};

const DomainItem = ({ item, setLoading, domain, setDomain }) => {
  const lang = useLanguage((s) => s.language);
  return (
    <Accordion.Item value={item.domain}>
      <Accordion.Control bg={domain === item.domain ? "primary.0" : undefined}>
        <Group wrap="nowrap" pr={24}>
          <Checkbox
            size="lg"
            checked={domain === item.domain}
            onChange={(e) =>
              e.target.checked ? setDomain(item.domain) : setDomain(null)
            }
            onClick={(e) => e.stopPropagation()}
          />
          <Text size="lg" c={"primary"} fw={600}>
            {item.domain}
          </Text>
          <JobState item={item} />
        </Group>
      </Accordion.Control>

      <Accordion.Panel>
        {item.emails.length > 0 ? (
          item.emails.map((email) => (
            <Group
              key={email.id}
              style={{ border: "1px solid #00000016", borderRadius: 8 }}
              p={"sm"}
              bg={"#fff"}
              pr={16}
              mt={8}
            >
              <Text size="lg">
                {email.display_name} : {email.prefix}@{item.domain}
              </Text>

              <JobState item={email} />
            </Group>
          ))
        ) : (
          <Text size="lg" ta={"center"} c={"dimmed"}>
            {lang.ovh.step2.no_email}
          </Text>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
};

const JobState = ({ item }) => {
  const lang = useLanguage((s) => s.language);
  if (item.state === "InQueue") {
    return (
      <Tooltip label={lang.ovh.step2.in_queue}>
        <Loader size={24} type="dots" color="orange" />
      </Tooltip>
    );
  }
  if (item.state === "InProgress") {
    return (
      <Tooltip label={lang.ovh.step2.in_progress}>
        <Loader size={18} color="primary" />
      </Tooltip>
    );
  }
  if (item.state === "Completed") {
    return (
      <Tooltip label={lang.ovh.step2.completed}>
        <ThemeIcon size={24} variant="subtle" color="green.8">
          <IconCheck />
        </ThemeIcon>
      </Tooltip>
    );
  }
};

export default OvhDomainList;
